import {
    Button,
    Variant,
} from '@genomics-dev/denim-components';
import {
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import {
    FormattedDate,
    FormattedMessage,
    useIntl,
} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {
    BackLink,
    FetchError,
    FetchValidationError,
    Loading,
    Test,
} from '../../../components';
import {useAmplitude} from '../../../context';
import {
    useMutation,
    useQuery,
    useTraitEligibility,
} from '../../../hooks';
import {useLinearFlowContext} from '../../../layouts';
import {Routes} from '../../../Routes';
import {
    Endpoint,
    extractErrorMessage,
} from '../../../util';

export function YourReports() {
    const amplitude = useAmplitude();

    const intl = useIntl();
    const {previousStep} = useLinearFlowContext();
    const {
        error: confirmError,
        isPending: isConfirmPending,
        mutateAsync: confirmTestReorder,
    } = useMutation({
        endpoint: Endpoint.REORDER_TEST,
        updateCache: true,
        updateCacheKey: [Endpoint.SUMMARY],
    });
    // Get the test order expiry date and don't use a cached value in case the date of birth or today's date has changed
    const {data: testOrderExpiryData} = useQuery({
        endpoint: Endpoint.TEST_ORDER_EXPIRY,
        staleTime: 0,
    });
    const navigate = useNavigate();
    const {
        eligibleTraits,
        error: traitError,
        ineligibleTraits,
        loading: traitLoading,
    } = useTraitEligibility();

    useEffect(() => {
        amplitude.logRequestNewTestYourResultsVisit();
    }, [amplitude]);

    const expiryDate = useMemo(() => (
        testOrderExpiryData?.expiryDate
            ? (
                <FormattedDate
                    day={'2-digit'}
                    month={'long'}
                    timeZone={'UTC'}
                    value={new Date(testOrderExpiryData.expiryDate)}
                    year={'numeric'}
                />
            )
            : null
    ), [testOrderExpiryData]);

    const onBackClickHandler = useCallback(() => {
        previousStep();
    }, [previousStep]);

    const onClickHandler = useCallback(async () => {
        await confirmTestReorder();
        amplitude.logRequestNewTestPlaced();
        navigate(Routes.HOME);
    }, [amplitude, navigate]);

    if (traitLoading) {
        return <Loading />;
    }

    if (traitError) {
        return <FetchError reason={extractErrorMessage(traitError)} />;
    }

    return (
        <>
            <BackLink
                className={'less-margin'}
                onClick={onBackClickHandler}
            />
            <h2 data-test-id={'test-reorder-your-reports-title'}>
                <FormattedMessage id={'testReorder.yourReports.title'} />
            </h2>
            <FetchValidationError errorResponse={confirmError} />
            {Boolean(eligibleTraits?.length) && (
                <div className={'test-block'}>
                    <div className={'is-caption is-heavy'}>
                        <FormattedMessage id={'common.profile.tests.included'} />
                    </div>
                    {eligibleTraits.map(({trait}) => (
                        <Test
                            dataTestId={`included-trait-${trait}`}
                            included={true}
                            key={trait}
                            trait={trait}
                        />
                    ))}
                </div>
            )}
            {Boolean(ineligibleTraits?.length) && (
                <div className={'test-block'}>
                    <div className={'is-caption is-heavy'}>
                        <FormattedMessage id={'common.profile.tests.notIncluded'} />
                    </div>
                    {ineligibleTraits.map(({trait}) => (
                        <Test
                            dataTestId={`not-included-trait-${trait}`}
                            included={false}
                            key={trait}
                            trait={trait}
                        />
                    ))}
                </div>
            )}
            {expiryDate && (
                <p data-test-id={'test-reorder-your-reports-expiry-date'}>
                    <FormattedMessage
                        id={'signUp.myReports.expiryDate'}
                        values={{expiryDate}}
                    />
                </p>
            )}
            <Button
                dataTestId={'test-reorder-order-new-test'}
                isLoading={isConfirmPending}
                onClick={onClickHandler}
                title={intl.formatMessage({id: 'testReorder.yourReports.orderNewTest.title'})}
                variant={Variant.PRIMARY}
            >
                <FormattedMessage id={'testReorder.yourReports.orderNewTest.text'} />
            </Button>
        </>
    );
}
